/**
 * EmbeddedConfigProvider - Config provider for export mode
 *
 * Returns configuration that was embedded at export time.
 * No Firestore access, no password protection, no authentication.
 */

import type { IConfigProvider, LoadedConfig } from '../interfaces';
import type { ExperienceConfig } from '../../types/config';
import { Logger } from '../../utils/Logger';

const logger = Logger.getLogger('EmbeddedConfigProvider');

export class EmbeddedConfigProvider implements IConfigProvider {
  private config: ExperienceConfig;
  private sequenceId: string | null = null;

  /**
   * Create an embedded config provider
   * @param config The experience configuration embedded in the export
   * @param sequenceId Optional sequence ID for reference (not used for fetching)
   */
  constructor(config: ExperienceConfig, sequenceId?: string) {
    this.config = config;
    this.sequenceId = sequenceId || null;
  }

  /**
   * Load the embedded configuration
   * Simply returns the config that was passed in at construction
   */
  async loadConfig(): Promise<LoadedConfig> {
    logger.debug('Returning embedded config');

    return {
      config: this.config,
      // No metadata in export mode - all features enabled, no user tracking
      metadata: undefined,
    };
  }

  /**
   * Exported packages are not password protected
   * (The export process only happens for users who already have access)
   */
  async requiresPassword(): Promise<boolean> {
    return false;
  }

  /**
   * Always returns true since exports don't have password protection
   */
  async verifyPassword(_password: string): Promise<boolean> {
    return true;
  }

  /**
   * Get the sequence ID (may be null in export mode)
   */
  getSequenceId(): string | null {
    return this.sequenceId;
  }
}
